import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  btnPrimaryClass,
  btnSecondaryClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  alertErrorClass,
  alertInfoClass,
  badgeClass,
  itemBadgeClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  spinnerClass,
} from "../dashboardStyles";
import {
  GetSupportTicketsAction,
  GetSupportTicketAction,
  CreateSupportTicketAction,
  AddSupportTicketMessageAction,
  UpdateSupportTicketStatusAction,
  getUserInfoFromToken,
} from "../../../store/action";
import { UserRole } from "../../../constants/roles";
import {
  SupportTicketCategory,
  SupportTicketStatus,
  SupportTicketCategoryLabelKeys,
  SupportTicketStatusLabelKeys,
  SupportTicketStatusBadgeClass,
  SUPPORT_IMAGE_MAX_BYTES,
} from "../../../constants/enums";
import { notify, notifySaveError } from "../../../utils/notify";

/**
 * Support page.
 *
 * One page, two faces, decided by the caller's role (read from the token, the
 * same blob the API trusts):
 *  - Company admins and employees open tickets and follow their own.
 *  - System owner and assigned admins triage: change status and reply.
 *
 * Deciding *which* admin covers an organization is no longer done here — it is
 * a property of the company, so it lives on the Companies page with everything
 * else about one.
 *
 * The API re-derives scope on every call, so what renders here is only what a
 * role is *offered*.
 */
const Support = () => {
  const { t } = useTranslation();

  const info = getUserInfoFromToken();
  const role = info?.role;
  const currentUserId = info?.userId;
  const isStaff = role === UserRole.SYSTEM_OWNER || role === UserRole.ADMIN;
  const canCreate =
    role === UserRole.COMPANY_ADMIN || role === UserRole.COMPANY_EMPLOYEE;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create-ticket modal
  const [showCreate, setShowCreate] = useState(false);
  const emptyForm = {
    category: SupportTicketCategory.BUG,
    subject: "",
    description: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  // Loading the ticket failed. A failed reply or status change is a snackbar.
  const [detailError, setDetailError] = useState(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetSupportTicketsAction();
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(t("Dashboard.Support.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // --- Create ---------------------------------------------------------------

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setImageFile(null);
      setImageError(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setImageError(t("Dashboard.Support.errors.imageType"));
      setImageFile(null);
      return;
    }
    if (file.size > SUPPORT_IMAGE_MAX_BYTES) {
      setImageError(t("Dashboard.Support.errors.imageSize"));
      setImageFile(null);
      return;
    }
    setImageError(null);
    setImageFile(file);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setImageFile(null);
    setImageError(null);
    setShowCreate(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      notify.warning(t("Dashboard.Support.errors.required"));
      return;
    }
    setSubmitting(true);
    try {
      await CreateSupportTicketAction(
        {
          category: Number(form.category),
          subject: form.subject.trim(),
          description: form.description.trim(),
        },
        imageFile,
      );
      setShowCreate(false);
      notify.success(t("Dashboard.Support.notices.created"));
      await loadTickets();
    } catch (err) {
      notifySaveError(err, t("Dashboard.Support.errors.create"));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Detail / reply / status ---------------------------------------------

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailError(null);
    setReplyBody("");
    setSelected({ id });
    try {
      const data = await GetSupportTicketAction(id);
      setSelected(data);
    } catch (err) {
      setDetailError(t("Dashboard.Support.errors.load"));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetailError(null);
    setReplyBody("");
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !selected?.id) return;
    setReplying(true);
    try {
      const updated = await AddSupportTicketMessageAction(
        selected.id,
        replyBody.trim(),
      );
      setSelected(updated);
      setReplyBody("");
      // Reflect the new activity (updatedAt / message count) in the list.
      setTickets((prev) =>
        prev.map((ti) => (ti.id === updated.id ? { ...ti, ...updated } : ti)),
      );
      notify.success(t("Dashboard.Support.notices.replySent"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Support.errors.reply"));
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected?.id) return;
    try {
      const updated = await UpdateSupportTicketStatusAction(
        selected.id,
        Number(newStatus),
      );
      setSelected(updated);
      setTickets((prev) =>
        prev.map((ti) => (ti.id === updated.id ? { ...ti, ...updated } : ti)),
      );
      notify.success(t("Dashboard.Support.notices.statusUpdated"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Support.errors.status"));
    }
  };

  // --- Render helpers -------------------------------------------------------

  const categoryLabel = (c) =>
    t(SupportTicketCategoryLabelKeys[c] ?? SupportTicketCategoryLabelKeys[2]);

  const statusBadge = (s) => (
    <span
      className={`${badgeClass} ${
        SupportTicketStatusBadgeClass[s] ?? "bg-ink-200 text-ink-600"
      }`}
    >
      {t(SupportTicketStatusLabelKeys[s] ?? SupportTicketStatusLabelKeys[0])}
    </span>
  );

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const statusOptions = [
    SupportTicketStatus.OPEN,
    SupportTicketStatus.IN_PROGRESS,
    SupportTicketStatus.RESOLVED,
    SupportTicketStatus.CLOSED,
  ];

  // --- Render ---------------------------------------------------------------

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitleClass}>{t("Dashboard.Support.title")}</h1>
          <p className={pageSubtitleClass}>
            {isStaff
              ? t("Dashboard.Support.subtitleStaff")
              : t("Dashboard.Support.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <button
              type="button"
              className={btnPrimaryClass}
              onClick={openCreate}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              {t("Dashboard.Support.newTicket")}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className={spinnerClass} role="status" aria-label={t("Dashboard.Menu.loading")} />
        </div>
      ) : tickets.length === 0 ? (
        <div className={alertInfoClass}>
          {canCreate
            ? t("Dashboard.Support.emptySubmitter")
            : t("Dashboard.Support.emptyStaff")}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {tickets.map((ticket) => (
            <button
              key={ticket.id}
              type="button"
              onClick={() => openDetail(ticket.id)}
              className="w-full rounded-card border border-ink-200 bg-white p-4 text-left shadow-card transition hover:border-brand-600 hover:shadow-card-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={itemBadgeClass}>
                  {categoryLabel(ticket.category)}
                </span>
                {statusBadge(ticket.status)}
                {ticket.imageUrl && (
                  <span className={itemBadgeClass}>
                    <i className="bi bi-paperclip" aria-hidden="true" />
                  </span>
                )}
                <span className="ml-auto text-xs text-ink-400">
                  {formatDate(ticket.updatedAt || ticket.createdAt)}
                </span>
              </div>
              <h3 className="mt-2 text-base font-semibold text-ink-900">
                {ticket.subject}
              </h3>
              <p className="mt-1 line-clamp-2 text-fluid-sm text-ink-500">
                {ticket.description}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
                {isStaff && ticket.organizationName && (
                  <span>
                    <i className="bi bi-building" aria-hidden="true" />{" "}
                    {ticket.organizationName}
                  </span>
                )}
                <span>
                  <i className="bi bi-person" aria-hidden="true" />{" "}
                  {ticket.createdByName}
                </span>
                {ticket.messageCount > 0 && (
                  <span>
                    <i className="bi bi-chat-dots" aria-hidden="true" />{" "}
                    {ticket.messageCount}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create ticket modal */}
      {showCreate && (
        <div className={modalOverlayClass} role="dialog" aria-modal="true">
          <div className={modalPanelClass}>
            <form onSubmit={handleCreateSubmit} className="flex min-h-0 flex-col">
              <div className={modalHeaderClass}>
                <h2 className="m-0 text-lg font-semibold text-ink-900">
                  {t("Dashboard.Support.newTicket")}
                </h2>
                <button
                  type="button"
                  className={modalCloseClass}
                  onClick={() => setShowCreate(false)}
                  aria-label={t("Dashboard.Support.close")}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
              <div className={modalBodyClass}>
                <div className="mb-4">
                  <label className={formLabelClass} htmlFor="ticket-category">
                    {t("Dashboard.Support.fields.category")}
                  </label>
                  <select
                    id="ticket-category"
                    className={formControlClass}
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                  >
                    <option value={SupportTicketCategory.BUG}>
                      {t("Dashboard.Support.category.bug")}
                    </option>
                    <option value={SupportTicketCategory.FEATURE_REQUEST}>
                      {t("Dashboard.Support.category.featureRequest")}
                    </option>
                    <option value={SupportTicketCategory.OTHER}>
                      {t("Dashboard.Support.category.other")}
                    </option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className={formLabelClass} htmlFor="ticket-subject">
                    {t("Dashboard.Support.fields.subject")}
                    <span className={requiredMarkClass}> *</span>
                  </label>
                  <input
                    id="ticket-subject"
                    type="text"
                    maxLength={200}
                    className={formControlClass}
                    value={form.subject}
                    onChange={(e) =>
                      setForm({ ...form, subject: e.target.value })
                    }
                    placeholder={t("Dashboard.Support.fields.subjectPlaceholder")}
                  />
                </div>
                <div className="mb-4">
                  <label className={formLabelClass} htmlFor="ticket-description">
                    {t("Dashboard.Support.fields.description")}
                    <span className={requiredMarkClass}> *</span>
                  </label>
                  <textarea
                    id="ticket-description"
                    rows={5}
                    maxLength={4000}
                    className={formControlClass}
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    placeholder={t(
                      "Dashboard.Support.fields.descriptionPlaceholder",
                    )}
                  />
                </div>
                <div>
                  <label className={formLabelClass} htmlFor="ticket-image">
                    {t("Dashboard.Support.fields.image")}
                  </label>
                  <input
                    id="ticket-image"
                    type="file"
                    accept="image/*"
                    className={formControlClass}
                    onChange={handleImageChange}
                  />
                  <p className="mt-1 text-xs text-ink-400">
                    {t("Dashboard.Support.fields.imageHint")}
                  </p>
                  {imageError && (
                    <p className="mt-1 text-xs text-red-600">{imageError}</p>
                  )}
                  {imageFile && (
                    <p className="mt-1 text-xs text-ink-500">{imageFile.name}</p>
                  )}
                </div>
              </div>
              <div className={modalFooterClass}>
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={() => setShowCreate(false)}
                >
                  {t("Dashboard.Support.cancel")}
                </button>
                <button
                  type="submit"
                  className={btnPrimaryClass}
                  disabled={submitting}
                >
                  {submitting
                    ? t("Dashboard.Support.submitting")
                    : t("Dashboard.Support.submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket detail modal */}
      {selected && (
        <div className={modalOverlayClass} role="dialog" aria-modal="true">
          <div className={modalPanelClass}>
            <div className={modalHeaderClass}>
              <h2 className="m-0 truncate text-lg font-semibold text-ink-900">
                {selected.subject || t("Dashboard.Support.title")}
              </h2>
              <button
                type="button"
                className={modalCloseClass}
                onClick={closeDetail}
                aria-label={t("Dashboard.Support.close")}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
            <div className={modalBodyClass}>
              {detailLoading ? (
                <div className="flex justify-center py-10">
                  <div className={spinnerClass} role="status" />
                </div>
              ) : (
                <>
                  {detailError && (
                    <div className={alertErrorClass} role="alert">
                      {detailError}
                    </div>
                  )}
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className={itemBadgeClass}>
                      {categoryLabel(selected.category)}
                    </span>
                    {statusBadge(selected.status)}
                    <span className="text-xs text-ink-400">
                      {formatDate(selected.createdAt)}
                    </span>
                  </div>

                  {/* Staff status control */}
                  {isStaff && (
                    <div className="mb-4">
                      <label className={formLabelClass} htmlFor="ticket-status">
                        {t("Dashboard.Support.changeStatus")}
                      </label>
                      <select
                        id="ticket-status"
                        className={formControlClass}
                        value={selected.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {t(SupportTicketStatusLabelKeys[s])}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mb-4 rounded-lg border border-ink-200 bg-ink-50 p-3 text-fluid-sm text-ink-500">
                    {isStaff && selected.organizationName && (
                      <div className="mb-1">
                        <i className="bi bi-building" aria-hidden="true" />{" "}
                        {selected.organizationName}
                      </div>
                    )}
                    <div>
                      <i className="bi bi-person" aria-hidden="true" />{" "}
                      {selected.createdByName}
                    </div>
                  </div>

                  <p className="mb-4 whitespace-pre-wrap text-fluid-sm text-ink-800">
                    {selected.description}
                  </p>

                  {selected.imageUrl && (
                    <a
                      href={selected.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-4 block"
                    >
                      <img
                        src={selected.imageUrl}
                        alt={t("Dashboard.Support.fields.image")}
                        className="max-h-64 w-auto max-w-full rounded-lg border border-ink-200"
                      />
                    </a>
                  )}

                  {/* Conversation */}
                  <h3 className="mb-2 text-sm font-semibold text-ink-700">
                    {t("Dashboard.Support.conversation")}
                  </h3>
                  {(!selected.messages || selected.messages.length === 0) && (
                    <p className="text-fluid-sm text-ink-400">
                      {t("Dashboard.Support.noMessages")}
                    </p>
                  )}
                  <div className="flex flex-col gap-3">
                    {(selected.messages || []).map((m) => {
                      const mine = m.authorUserId === currentUserId;
                      const staffAuthor =
                        m.authorRole === UserRole.SYSTEM_OWNER ||
                        m.authorRole === UserRole.ADMIN;
                      return (
                        <div
                          key={m.id}
                          className={`rounded-lg border p-3 ${
                            staffAuthor
                              ? "border-brand-200 bg-brand-50"
                              : "border-ink-200 bg-white"
                          }`}
                        >
                          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-ink-500">
                            <span className="font-semibold text-ink-700">
                              {m.authorName}
                              {mine && ` (${t("Dashboard.Support.you")})`}
                            </span>
                            <span>{formatDate(m.createdAt)}</span>
                          </div>
                          <p className="whitespace-pre-wrap text-fluid-sm text-ink-800">
                            {m.body}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {!detailLoading && (
              <div className="shrink-0 border-t border-ink-200 p-4">
                <form onSubmit={handleReply} className="flex flex-col gap-2">
                  <textarea
                    rows={2}
                    maxLength={4000}
                    className={formControlClass}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder={t("Dashboard.Support.replyPlaceholder")}
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className={btnPrimaryClass}
                      disabled={replying || !replyBody.trim()}
                    >
                      {replying
                        ? t("Dashboard.Support.sending")
                        : t("Dashboard.Support.sendReply")}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
